import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showNotes } from "../redux/windowSlice";
import { setCurrentApp, setCurrentSettings } from "../redux/appSlice";
import Resume from "./Resume";
import TrafficLights from "./TrafficLights";
import WindowFrame from "./WindowFrame";
import useGenie from "../hooks/useGenie";
import useClock, { formatNoteDate } from "../hooks/useClock";
import { FOLDERS, NOTES, datedNotes, groupByDate, rowDate } from "../data/notes";
import { ReactComponent as QuickNoteIcon } from "../assets/svgs/sf-symbols/notes/quicknote.svg";
import { ReactComponent as SharedIcon } from "../assets/svgs/sf-symbols/notes/person.crop.circle.svg";
import { ReactComponent as FolderIcon } from "../assets/svgs/sf-symbols/notes/folder.svg";
import { ReactComponent as NewFolderIcon } from "../assets/svgs/sf-symbols/notes/folder.badge.plus.svg";
import { ReactComponent as SidebarIcon } from "../assets/svgs/sf-symbols/notes/sidebar.left.svg";
import { ReactComponent as EllipsisIcon } from "../assets/svgs/sf-symbols/notes/ellipsis.svg";
import { ReactComponent as ComposeIcon } from "../assets/svgs/sf-symbols/notes/square.and.pencil.svg";
import { ReactComponent as ChecklistIcon } from "../assets/svgs/sf-symbols/notes/checklist.svg";
import { ReactComponent as TableIcon } from "../assets/svgs/sf-symbols/notes/tablecells.svg";
import { ReactComponent as AttachIcon } from "../assets/svgs/sf-symbols/notes/paperclip.svg";
import { ReactComponent as ShareIcon } from "../assets/svgs/sf-symbols/notes/square.and.arrow.up.svg";
import { ReactComponent as SearchIcon } from "../assets/svgs/sf-symbols/notes/magnifyingglass.svg";
import { ReactComponent as ChevronDown } from "../assets/svgs/sf-symbols/notes/chevron.down.svg";
import { ReactComponent as LockIcon } from "../assets/svgs/sf-symbols/notes/lock.fill.svg";

const FOLDER_ICONS = { quicknote: QuickNoteIcon, shared: SharedIcon, folder: FolderIcon };
const NOTES_MENUS = ["File", "Edit", "Format", "View", "Window", "Help"];

// Default frame: up to 1060 x 680, centered in the workspace.
const notesFrame = (ws) => {
  const w = Math.min(1060, ws.w - 64);
  const h = Math.min(680, ws.h - 24);
  return { x: (ws.w - w) / 2, y: (ws.h - h) / 2, w, h };
};

// Notes, laid out like macOS Tahoe's: a floating folder sidebar, then the note list grouped by
// date, then the note itself, under one toolbar.
const NotesWindow = ({ workspace, front, onFocus }) => {
  const { snotes } = useSelector((state) => state.window);
  const disptach = useDispatch();
  const now = useClock(30000);

  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [exit, setExit] = useState("closed");
  const genie = useGenie({ app: "Notes", open: snotes, exit });
  const [folderId, setFolderId] = useState("notes");
  const [selected, setSelected] = useState("resume");
  const [query, setQuery] = useState("");

  const dated = useMemo(
    () => datedNotes(now),
    // Re-date only when the day changes, not every clock tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [now.toDateString()]
  );

  const folder = FOLDERS.find((f) => f.id === folderId);
  const inFolder = folder.notes(dated);
  const q = query.trim().toLowerCase();
  const shown = q ? inFolder.filter((n) => `${n.title} ${n.preview}`.toLowerCase().includes(q)) : inFolder;
  const groups = groupByDate(shown, now);
  const current = inFolder.find((n) => n.id === selected) || null;

  const openFolder = (id) => {
    setFolderId(id);
    setQuery("");
    const first = FOLDERS.find((f) => f.id === id).notes(dated)[0];
    setSelected(first ? first.id : null);
  };

  // Spotlight, Finder and the desktop icon ask for the resume with
  // `window.dispatchEvent(new CustomEvent("open-resume", { detail: { anchor } }))`: open Notes on
  // it, then scroll to and briefly highlight the anchor.
  useEffect(() => {
    const onOpen = (e) => {
      setFolderId((id) => (FOLDERS.find((f) => f.id === id).notes(NOTES).some((n) => n.id === "resume") ? id : "notes"));
      setQuery("");
      setSelected("resume");
      if (!snotes) disptach(showNotes(true));
      disptach(setCurrentApp("Notes"));
      disptach(setCurrentSettings(NOTES_MENUS));
      const anchor = e.detail && e.detail.anchor;
      if (!anchor) return;
      setTimeout(
        () => {
          const el = document.getElementById(anchor);
          if (!el) return;
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.classList.remove("resume-flash");
          void el.offsetWidth;
          el.classList.add("resume-flash");
        },
        snotes ? 50 : 450
      );
    };
    window.addEventListener("open-resume", onOpen);
    return () => window.removeEventListener("open-resume", onOpen);
  }, [snotes, disptach]);

  const hide = (how) => {
    setExit(how);
    if (how === "closed") setMaximized(false);
    disptach(showNotes(!snotes));
    disptach(setCurrentApp("Finder"));
    disptach(setCurrentSettings(["File", "Edit", "View", "Go", "Windows", "Help"]));
  };

  const state = snotes ? "nw--open" : `nw--${exit}`;
  const sidebarToggle = (
    <button
      type="button"
      className="na-tool"
      onClick={() => setSidebarHidden(!sidebarHidden)}
      aria-label={sidebarHidden ? "Show sidebar" : "Hide sidebar"}
      aria-pressed={!sidebarHidden}
    >
      <SidebarIcon className="na-icon-sidebar" />
    </button>
  );

  return (
    <WindowFrame
      workspace={workspace}
      initial={notesFrame}
      min={{ w: 760, h: 420 }}
      open={snotes}
      front={front}
      zoomed={maximized}
      onFocus={onFocus}
      onToggleZoom={() => setMaximized((m) => !m)}
    >
      <div
        id="notes"
        role="dialog"
        aria-label="Notes"
        aria-hidden={!snotes}
        ref={genie.ref}
        className={`nw notes-app ${state} ${maximized ? "nw--max" : ""} ${front ? "is-front" : ""} ${
          sidebarHidden ? "is-sidebar-hidden" : ""
        } ${genie.hidden ? "is-genie-hidden" : ""}`}
      >
        <div className="traffic">
          <TrafficLights
            onClose={() => hide("closed")}
            onMinimize={() => genie.minimize(() => hide("minimized"))}
            onPrepare={genie.prepare}
            onZoom={() => setMaximized(!maximized)}
            zoomed={maximized}
          />
        </div>

        <aside className={`nw-sidebar na-sidebar no-select ${sidebarHidden ? "is-collapsed" : ""}`}>
          <div className="na-sidebar-tools">
            <span className="na-tool" aria-hidden="true">
              <NewFolderIcon className="na-icon-newfolder" />
            </span>
            {sidebarToggle}
          </div>
          {FOLDERS.map((f, i) => {
            const Icon = FOLDER_ICONS[f.icon];
            const active = f.id === folderId;
            return (
              <React.Fragment key={f.id}>
                {f.section && <p className="na-section">{f.section}</p>}
                <button
                  type="button"
                  onClick={() => openFolder(f.id)}
                  className={`na-folder ${active ? "is-active" : ""} ${f.icon === "folder" ? "is-folder" : ""}`}
                  aria-current={active}
                >
                  <Icon className="na-folder-icon" aria-hidden="true" />
                  <span className="flex-1 truncate">{f.title}</span>
                  <span className="na-count">{f.notes(dated).length}</span>
                </button>
              </React.Fragment>
            );
          })}
        </aside>

        <div className="na-body">
          <header className="na-toolbar no-select">
            {sidebarHidden && <div className="na-toolbar-lead">{sidebarToggle}</div>}
            <div className="na-heading">
              <h2>{folder.title}</h2>
              <p>
                {inFolder.length} {inFolder.length === 1 ? "note" : "notes"}
              </p>
            </div>
            <span className="na-tool na-round na-glass" aria-hidden="true">
              <EllipsisIcon className="na-icon-ellipsis" />
            </span>
            <span className="na-divider" aria-hidden="true" />
            <span className="na-tool na-round na-glass" aria-hidden="true">
              <ComposeIcon className="na-icon-compose" />
            </span>
            <div className="na-capsule na-format na-glass" aria-hidden="true">
              <span className="na-tool na-aa">Aa</span>
              <span className="na-tool">
                <ChecklistIcon className="na-icon-checklist" />
              </span>
              <span className="na-tool">
                <TableIcon className="na-icon-table" />
              </span>
              <span className="na-tool">
                <AttachIcon className="na-icon-attach" />
              </span>
            </div>
            <div className="na-capsule na-share na-glass" aria-hidden="true">
              <span className="na-tool">
                <ShareIcon className="na-icon-share" />
              </span>
              <span className="na-tool">
                <EllipsisIcon className="na-icon-ellipsis" />
              </span>
            </div>
            <label className="na-search na-glass">
              <SearchIcon className="na-icon-search" aria-hidden="true" />
              <ChevronDown className="na-icon-chevron" aria-hidden="true" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                aria-label={`Search ${folder.title}`}
              />
            </label>
          </header>

          <div className="na-columns">
            <nav className="na-list no-select" aria-label={`${folder.title} notes`}>
              {groups.map((g) => (
                <section key={g.name}>
                  <h3 className="na-group">{g.name}</h3>
                  {g.notes.map((note) => (
                    <button
                      type="button"
                      key={note.id}
                      onClick={() => setSelected(note.id)}
                      className={`na-row ${note.id === selected ? "is-selected" : ""}`}
                      aria-current={note.id === selected}
                    >
                      <span className="na-row-title">{note.title}</span>
                      <span className="na-row-meta">
                        <span className="na-row-date">{rowDate(note.date, now)}</span>
                        <span className="na-row-preview">{note.preview}</span>
                      </span>
                    </button>
                  ))}
                </section>
              ))}
              {!shown.length && <p className="na-empty">{q ? "No Results" : "No Notes"}</p>}
            </nav>

            <main className="nw-scroll na-content">
              {current && current.id === "resume" && (
                <div className="edit-cursor editor-main">
                  <p className="note-date">{formatNoteDate(current.date)}</p>
                  <Resume />
                </div>
              )}
              {current && current.id !== "resume" && (
                <div className="locked-note no-select">
                  <LockIcon className="na-lock" aria-hidden="true" />
                  <p className="mt-4 text-[17px] font-semibold">This note is locked.</p>
                  <p className="mt-1 text-[13px] text-[#6e6e73]">Only my resume is unlocked.</p>
                  <button
                    type="button"
                    onClick={() => openFolder("notes")}
                    className="mt-5 h-8 px-4 rounded-full bg-[#e3ae0a] text-[13px] font-semibold text-black/85"
                  >
                    Open my resume
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </WindowFrame>
  );
};

export default NotesWindow;
